import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NativeButtonWithIcon } from '@/components/common/NativeButtonWithIcon';
import { cn } from '@/lib/utils';

type CommonModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly titleId?: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly overlayClassName?: string;
  readonly panelClassName?: string;
  readonly bodyClassName?: string;
  readonly footerClassName?: string;
  readonly hideCloseButton?: boolean;
};

export function CommonModal({
  open,
  onClose,
  title,
  titleId,
  children,
  footer,
  overlayClassName,
  panelClassName,
  bodyClassName,
  footerClassName,
  hideCloseButton = false,
}: CommonModalProps) {
  if (!open) return null;

  const resolvedTitleId = titleId ?? 'common-modal-title';
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn('fixed inset-0 z-50 bg-black/50', overlayClassName)}
    >
      <div className="absolute inset-0 flex min-h-0 items-center justify-center px-4 pb-4 pt-0">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={resolvedTitleId}
          className={cn(
            'glass-card flex max-h-[90vh] w-full flex-col overflow-x-hidden p-6 shadow-xl',
            panelClassName,
          )}
        >
          <div className="mb-4 flex shrink-0 items-center justify-between">
            <h2 id={resolvedTitleId} className="text-xl font-semibold">
              {title}
            </h2>
            {hideCloseButton ? null : (
              <NativeButtonWithIcon
                type="button"
                onClick={onClose}
                icon="x"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Close
              </NativeButtonWithIcon>
            )}
          </div>
          <div
            className={cn('min-h-0 flex-1 overflow-y-auto overflow-x-hidden', bodyClassName)}
          >
            {children}
          </div>
          {footer ? (
            <div className={cn('mt-4 shrink-0 border-t border-border pt-2', footerClassName)}>{footer}</div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
